import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { Type } from './text';

/**
 * Spinner – rotating sun-style icon (from LLMTalk).
 * Use className for size (e.g. h-4 w-4, h-8 w-8) and color (e.g. text-primary, text-foreground).
 */
function Spinner({ className, size = 'md', ...props }) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };
  return (
    <svg
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('animate-spin stroke-current', sizeClasses[size], className)}
      aria-hidden
      {...props}
    >
      <path d="M12 3v3m6.366-.366-2.12 2.12M21 12h-3m.366 6.366-2.12-2.12M12 21v-3m-6.366.366 2.12-2.12M3 12h3m-.366-6.366 2.12 2.12" />
    </svg>
  );
}

const loadingDotStyle = {
  display: 'block',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
};

const loadingContainerStyle = {
  width: '24px',
  height: '24px',
  paddingTop: '6px',
  display: 'flex',
  justifyContent: 'space-around',
};

const containerVariants = {
  initial: { transition: { staggerChildren: 0.2 } },
  animate: { transition: { staggerChildren: 0.2 } },
};

const dotVariants = {
  initial: { y: '0%', opacity: 1 },
  animate: { y: '100%', opacity: 0.5 },
};

const dotTransition = { duration: 1.5, repeat: Infinity, ease: 'easeInOut' };

/**
 * LinearSpinner – three bouncing dots (from LLMTalk, framer-motion).
 */
function LinearSpinner({ className }) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <motion.div
        style={loadingContainerStyle}
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        <motion.span
          style={loadingDotStyle}
          className="bg-muted-foreground"
          variants={dotVariants}
          transition={dotTransition}
        />
        <motion.span
          style={loadingDotStyle}
          className="bg-muted-foreground"
          variants={dotVariants}
          transition={dotTransition}
        />
        <motion.span
          style={loadingDotStyle}
          className="bg-muted-foreground"
          variants={dotVariants}
          transition={dotTransition}
        />
      </motion.div>
    </div>
  );
}

/**
 * FullPageLoader – centered Spinner with optional label (from LLMTalk).
 */
function FullPageLoader({ label, className }) {
  return (
    <div
      className={cn(
        'z-20 flex min-h-[90vh] w-full flex-1 flex-col items-center justify-center gap-1',
        className
      )}
    >
      <Spinner size="xl" className="text-primary" />
      {label && (
        <Type size="xs" textColor="secondary">
          {label}
        </Type>
      )}
    </div>
  );
}

export { Spinner, LinearSpinner, FullPageLoader };
